import { CSSProperties, FC, memo, useMemo } from "react";
import "./radio.scss";
import { FormInputElement } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";

interface RadioGroupComponentProps<T> {
  onChange?: (value: T) => void;
  value?: T;
  options?: T[];
  getKey: (value: T) => string | number;
  getValue: (value?: T) => string | number | undefined;
  getLabel: (value: T) => string;
  label?: string;
  name?: string;
  required?: boolean;
  errorMessage?: string;
  labelClassName?: string;
  optionClassName?: string;
  className?: string;
  infoText?: string;
  disabled?: boolean;
  varient?: string | "border" | "";
  selectedColor?: string;
}

const RadioGroupComponent = <T,>({
  onChange,
  value,
  name,
  getKey,
  getValue,
  getLabel,
  required,
  options,
  label,
  errorMessage,
  labelClassName,
  optionClassName,
  className,
  infoText,
  disabled,
  varient,
  selectedColor,
}: RadioGroupComponentProps<T>) => {
  const valueKey = getValue(value);
  return (
    <div className={`radio-group-container flex-wrap ${className}`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
      />
      <div className={`radio-group-wrap${varient ? ` ${varient}` : ""}`}>
        {options?.map((option) => {
          const optionValue = getValue(option);
          const optionKey = getKey(option);
          return (
            <RadioButton
              key={optionKey}
              value={optionValue}
              disabled={disabled}
              label={getLabel(option)}
              className={optionClassName}
              name={name}
              checked={optionKey === valueKey}
              onChange={() => onChange?.(option)}
              selectedColor={selectedColor}
            />
          );
        })}
      </div>

      <FormError errorMessage={errorMessage} />
    </div>
  );
};

export const RadioGroup = memo(RadioGroupComponent) as typeof RadioGroupComponent;

interface RadioButtonComponentProps extends FormInputElement {
  value?: string | number;
  checked: boolean;
  onChange: () => void;
  labelClass?: string;
  selectedColor?: string;
}

const RadioButtonComponent: FC<RadioButtonComponentProps> = ({
  label,
  className,
  labelClass,
  selectedColor,
  ...rest
}) => {
  const themedStyle = useMemo<CSSProperties | undefined>(() => {
    if (!selectedColor) {
      return undefined;
    }

    return {
      "--radio-selected-color": selectedColor,
    } as CSSProperties;
  }, [selectedColor]);

  return (
    <div className={`radio-container ${className} text-black`} style={themedStyle}>
      <label className={labelClass}>
        <input type="radio" {...rest} />
        <span className="radio" />
        {label && <span className="value">{label}</span>}
      </label>
    </div>
  );
};

export const RadioButton = memo(RadioButtonComponent);
